import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';

const SORT_PARAM = 'sort';
const FILTERS_PARAM = 'filters';

function parseSortParameter(value: string | null): GridSortModel {
  if (!value) return [];
  const [field, sort] = value.split(':');
  if (!field || !sort) return [];
  if (sort !== 'asc' && sort !== 'desc') return [];
  return [{ field, sort }];
}

function parseFiltersParameter(value: string | null): GridFilterModel | undefined {
  if (!value) return undefined;
  try {
    const items = JSON.parse(value);
    if (!Array.isArray(items)) return undefined;
    return { items };
  } catch {
    return undefined;
  }
}

function hasActiveFilterItems(model: GridFilterModel): boolean {
  return model.items.some(item => item.value !== undefined && item.value !== '');
}

export function useGridStateParameters() {
  const [searchParameters, setSearchParameters] = useSearchParams();

  const sortModel = useMemo(() => parseSortParameter(searchParameters.get(SORT_PARAM)), [searchParameters]);

  const filterModel = useMemo(() => parseFiltersParameter(searchParameters.get(FILTERS_PARAM)), [searchParameters]);

  const onSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      setSearchParameters(previous => {
        const next = new URLSearchParams(previous);
        if (newModel.length > 0 && newModel[0].sort) {
          next.set(SORT_PARAM, `${newModel[0].field}:${newModel[0].sort}`);
        } else {
          next.delete(SORT_PARAM);
        }
        return next;
      });
    },
    [setSearchParameters]
  );

  const onFilterModelChange = useCallback(
    (newModel: GridFilterModel) => {
      setSearchParameters(previous => {
        const next = new URLSearchParams(previous);
        if (newModel.items.length > 0 && hasActiveFilterItems(newModel)) {
          next.set(FILTERS_PARAM, JSON.stringify(newModel.items));
        } else {
          next.delete(FILTERS_PARAM);
        }
        return next;
      });
    },
    [setSearchParameters]
  );

  return { sortModel, filterModel, onSortModelChange, onFilterModelChange };
}
